import React, { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { api, formatApiErrorDetail } from "@/lib/api";
import { euro, num, dateIt } from "@/lib/format";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";
import { toast } from "sonner";
import { Plus, Fuel, Route, CalendarRange, AlertTriangle } from "lucide-react";
import { Checkbox } from "@/components/ui/checkbox";
import { isFestivo } from "@/components/SelettoreGiorni";

const isoLocale = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
const isWeekend = (iso) => [0, 6].includes(new Date(`${iso}T00:00:00`).getDay());
const today = () => isoLocale(new Date());
const giorniIntervallo = (dal, al) => {
  const out = [];
  const d = new Date(`${dal}T00:00:00`);
  const fine = new Date(`${al}T00:00:00`);
  while (d <= fine) { out.push(isoLocale(d)); d.setDate(d.getDate() + 1); }
  return out;
};
const vuoto = (v) => v === "" || v === null || v === undefined;

function Sezione({ icon: Icon, titolo, children, testid }) {
  return (
    <div className="rounded-lg border border-slate-200 bg-slate-50/60 p-3 space-y-2" data-testid={testid}>
      <div className="flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wider text-slate-600"><Icon className="h-3.5 w-3.5 text-primary" />{titolo}</div>
      {children}
    </div>
  );
}

export function VeicoliMultiGiorno({ commessaId, veicoli, impostazioni, onSaved }) {
  const qc = useQueryClient();
  const [f, setF] = useState({ veicolo_id: "", dal: today(), al: "", noleggio: "", km: "", litri: "", prezzo_litro: "", pedaggio: "", descrizione: "", solo_lavorativi: false });
  const [conferma, setConferma] = useState(null);
  const set = (k, v) => setF((s) => ({ ...s, [k]: v }));
  const alEff = f.al || f.dal;
  const veicolo = veicoli.find((v) => v.id === f.veicolo_id);
  const consumo = Number(veicolo?.consumo_l_100km) || 0;
  const { data: esistenti = [] } = useQuery({
    queryKey: ["veicoli-costi", commessaId],
    queryFn: async () => (await api.get(`/commesse/${commessaId}/veicoli-costi`)).data,
  });

  const setKm = (v) => setF((s) => ({ ...s, km: v, litri: consumo > 0 && v !== "" ? String(Math.round((Number(v) || 0) * consumo) / 100) : s.litri }));

  const mut = useMutation({
    mutationFn: async (righe) => {
      for (const r of righe) await api.post(`/commesse/${commessaId}/veicoli-costi`, r);
      return righe.length;
    },
    onSuccess: async (n) => {
      toast.success(`${n} righe costo veicolo aggiunte`);
      setF((s) => ({ ...s, noleggio: "", km: "", litri: "", prezzo_litro: "", pedaggio: "" }));
      await qc.refetchQueries({ queryKey: ["veicoli-costi", commessaId], type: "all" });
      onSaved?.();
    },
    onError: (e) => toast.error(formatApiErrorDetail(e.response?.data?.detail)),
  });

  const tutti = f.dal && alEff >= f.dal ? giorniIntervallo(f.dal, alEff) : [];
  const giorni = f.solo_lavorativi ? tutti.filter((g) => !isWeekend(g) && !isFestivo(new Date(`${g}T00:00:00`))) : tutti;
  const esclusi = tutti.length - giorni.length;
  const nol = Number(f.noleggio), km = Number(f.km), litri = Number(f.litri), prezzo = Number(f.prezzo_litro), ped = Number(f.pedaggio);
  const tipiAttivi = [nol > 0, km > 0 || litri > 0, ped > 0].filter(Boolean).length;
  const anteprimaGiorno = (nol > 0 ? nol : 0) + (litri > 0 ? litri * (prezzo || 0) : 0) + (ped > 0 ? ped : 0);

  const submit = () => {
    if (!f.veicolo_id) return toast.error("Seleziona un veicolo");
    if (!f.dal) return toast.error("Indica la data");
    if (alEff < f.dal) return toast.error("La data finale deve essere successiva o uguale a quella iniziale");
    if (vuoto(f.noleggio) || vuoto(f.km) || vuoto(f.litri) || vuoto(f.prezzo_litro) || vuoto(f.pedaggio))
      return toast.error("Compila tutti i campi di Noleggio, Carburante e Pedaggio: inserisci 0 dove non c'è costo");
    if ([nol, km, litri, prezzo, ped].some((v) => Number.isNaN(v) || v < 0)) return toast.error("I valori non possono essere negativi");
    if ((km > 0 || litri > 0) && (km <= 0 || litri <= 0 || prezzo <= 0)) return toast.error("Per il carburante servono km percorsi, litri e prezzo al litro maggiori di 0");
    if (tipiAttivi === 0) return toast.error("Tutti i costi sono a 0: nulla da registrare");
    if (giorni.length === 0) return toast.error("Nessun giorno lavorativo nell'intervallo selezionato");
    const righe = [];
    for (const data of giorni) {
      if (nol > 0) righe.push({ veicolo_id: f.veicolo_id, tipo: "noleggio", data, giorni: 1, costo_giornaliero: nol, descrizione: f.descrizione });
      if (km > 0) righe.push({ veicolo_id: f.veicolo_id, tipo: "carburante", data, km, litri, costo_litro: prezzo, descrizione: f.descrizione });
      if (ped > 0) righe.push({ veicolo_id: f.veicolo_id, tipo: "pedaggio", data, importo: ped, descrizione: f.descrizione });
    }
    const giaRegistrati = esistenti.filter((r) => r.veicolo_id === f.veicolo_id).map((r) => (r.data || "").slice(0, 10));
    const duplicati = giorni.filter((g) => giaRegistrati.includes(g));
    if (duplicati.length > 0) return setConferma({ righe, duplicati });
    mut.mutate(righe);
  };

  return (
    <div className="space-y-4" data-testid="vei-multi-form">
      <div className="grid sm:grid-cols-6 gap-3 items-end">
        <div className="space-y-1.5 sm:col-span-2">
          <Label>Veicolo</Label>
          <Select value={f.veicolo_id} onValueChange={(v) => { const vv = veicoli.find((x) => x.id === v); setF((s) => ({ ...s, veicolo_id: v, descrizione: vv?.descrizione || vv?.modello || "", noleggio: s.noleggio === "" && vv?.costo_giornaliero ? String(vv.costo_giornaliero) : s.noleggio, prezzo_litro: s.prezzo_litro === "" ? String(vv?.costo_litro || impostazioni?.costo_litro_default || "") : s.prezzo_litro })); }}>
            <SelectTrigger data-testid="vei-multi-veicolo-select"><SelectValue placeholder="Seleziona veicolo" /></SelectTrigger>
            <SelectContent>
              {veicoli.filter((v) => v.attivo !== false).map((v) => (
                <SelectItem key={v.id} value={v.id}>{v.targa} · {v.modello} — {euro(v.costo_giornaliero)}/g</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div className="space-y-1.5"><Label>Dal</Label><Input type="date" value={f.dal} onChange={(e) => set("dal", e.target.value)} data-testid="vei-multi-dal" /></div>
        <div className="space-y-1.5"><Label>Al <span className="text-slate-400 font-normal text-xs">(facoltativo)</span></Label><Input type="date" value={f.al} min={f.dal} placeholder={f.dal} onChange={(e) => set("al", e.target.value)} data-testid="vei-multi-al" /></div>
        <div className="space-y-1.5 sm:col-span-2"><Label>Descrizione</Label><Input value={f.descrizione} onChange={(e) => set("descrizione", e.target.value)} placeholder="es. Furgone cantiere" data-testid="vei-multi-descrizione" /></div>
        <label className="sm:col-span-6 flex items-center gap-2 text-sm text-slate-700 cursor-pointer" data-testid="vei-multi-solo-lavorativi-label">
          <Checkbox checked={f.solo_lavorativi} onCheckedChange={(v) => set("solo_lavorativi", !!v)} data-testid="vei-multi-solo-lavorativi" />
          Solo giorni lavorativi (escludi sabati, domeniche e festività italiane)
          {f.solo_lavorativi && tutti.length > 0 && <span className="text-xs text-slate-400">— {esclusi} giorn{esclusi === 1 ? "o" : "i"} esclus{esclusi === 1 ? "o" : "i"} su {tutti.length}</span>}
        </label>
      </div>

      <div className="grid sm:grid-cols-3 gap-3">
        <Sezione icon={CalendarRange} titolo="Noleggio" testid="vei-multi-sez-noleggio">
          <Label className="text-xs">Importo giornaliero (€) *</Label>
          <Input type="number" min="0" step="0.01" value={f.noleggio} onChange={(e) => set("noleggio", e.target.value)} placeholder="0" data-testid="vei-multi-noleggio" />
        </Sezione>
        <Sezione icon={Fuel} titolo="Carburante" testid="vei-multi-sez-carburante">
          <div className="grid grid-cols-3 gap-2">
            <div><Label className="text-xs">Km percorsi *</Label><Input type="number" min="0" value={f.km} onChange={(e) => setKm(e.target.value)} placeholder="0" data-testid="vei-multi-km" /></div>
            <div><Label className="text-xs">Litri *</Label><Input type="number" min="0" step="0.01" value={f.litri} onChange={(e) => set("litri", e.target.value)} placeholder="0" data-testid="vei-multi-litri" /></div>
            <div><Label className="text-xs">€/litro *</Label><Input type="number" min="0" step="0.001" value={f.prezzo_litro} onChange={(e) => set("prezzo_litro", e.target.value)} placeholder="0" data-testid="vei-multi-prezzo-litro" /></div>
          </div>
          <p className="text-[11px] text-slate-400" data-testid="vei-multi-consumo-hint">
            {veicolo ? (consumo > 0 ? `Consumo medio veicolo: ${num(consumo, 1)} l/100 km — i litri si calcolano dai km (modificabili).` : "Consumo medio non impostato in Anagrafica: inserisci i litri a mano.") : "Seleziona un veicolo per il calcolo automatico dei litri."}
          </p>
        </Sezione>
        <Sezione icon={Route} titolo="Pedaggio" testid="vei-multi-sez-pedaggio">
          <Label className="text-xs">Importo (€) *</Label>
          <Input type="number" min="0" step="0.01" value={f.pedaggio} onChange={(e) => set("pedaggio", e.target.value)} placeholder="0" data-testid="vei-multi-pedaggio" />
        </Sezione>
      </div>

      <div className="flex items-center justify-between flex-wrap gap-3">
        <div className="text-sm text-slate-500" data-testid="vei-multi-anteprima">
          {veicolo && giorni.length > 0 ? (
            <>{giorni.length} giorn{giorni.length === 1 ? "o" : "i"} × {tipiAttivi} tip{tipiAttivi === 1 ? "o" : "i"} di costo = <b>{giorni.length * tipiAttivi} righe</b> · circa <b className="text-primary font-mono">{euro(anteprimaGiorno * giorni.length)}</b> ({euro(anteprimaGiorno)}/giorno{litri > 0 ? `, ${num(litri, 2)} l/giorno` : ""})</>
          ) : "Seleziona veicolo e data (lascia vuoto \"Al\" per un giorno singolo). Tutti i campi costo sono obbligatori: inserisci 0 dove non c'è costo."}
        </div>
        <Button onClick={submit} disabled={mut.isPending} data-testid="vei-multi-add-button"><Plus className="h-4 w-4 mr-1" /> {mut.isPending ? "Salvataggio…" : "Registra su tutti i giorni"}</Button>
      </div>

      <AlertDialog open={!!conferma} onOpenChange={(v) => !v && setConferma(null)}>
        <AlertDialogContent data-testid="vei-multi-duplicati-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-2 text-amber-700"><AlertTriangle className="h-5 w-5" /> Attenzione: questo veicolo è già stato registrato per questa data</AlertDialogTitle>
            <AlertDialogDescription data-testid="vei-multi-duplicati-descr">
              {veicolo?.targa} risulta già registrato su questa commessa nei giorni: <b>{(conferma?.duplicati || []).map(dateIt).join(", ")}</b>.
              Vuoi registrare comunque le {conferma?.righe.length} righe?
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="vei-multi-duplicati-annulla">Annulla</AlertDialogCancel>
            <AlertDialogAction onClick={() => { mut.mutate(conferma.righe); setConferma(null); }} data-testid="vei-multi-duplicati-conferma">Registra comunque</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
